/**
 * Place検索・ソート共通ユーティリティ
 *
 * PlacesListSheet と PlacesMapPage で共有される型・定数・フィルタロジック
 */

import type { OverlandVisit, Place } from '@/api/types/overland'

// ============================================================================
// ソート
// ============================================================================

export type SortOption = 'visit_count' | 'last_visited' | 'name' | 'photo_count'

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'visit_count', label: '訪問回数' },
  { value: 'last_visited', label: '最終訪問日' },
  { value: 'name', label: '名前順' },
  { value: 'photo_count', label: 'メディア数' },
]

// ============================================================================
// テキスト検索フィルタ
// ============================================================================

/**
 * Place一覧をテキストクエリでフィルタリング
 * display_name, address, categories, genres を対象に部分一致検索
 */
export const filterPlacesByQuery = (places: Place[], query: string): Place[] => {
  if (!query) return places
  const q = query.toLowerCase()
  return places.filter(
    (place) =>
      place.display_name.toLowerCase().includes(q) ||
      place.address?.toLowerCase().includes(q) ||
      place.categories.some((c) => (c.display_name ?? c.name).toLowerCase().includes(q)) ||
      place.genres.some((g) => g.name.toLowerCase().includes(q)),
  )
}

// ============================================================================
// WiFi → 場所名推測マッピング
// ============================================================================

const WIFI_PLACE_MAP: { pattern: string; name: string }[] = [
  { pattern: 'kaonm', name: '自宅' },
  { pattern: 'home', name: '自宅' },
  { pattern: 'starbucks', name: 'スターバックス' },
  { pattern: 'スタバ', name: 'スターバックス' },
  { pattern: 'doutor', name: 'ドトール' },
  { pattern: 'tullys', name: 'タリーズ' },
  { pattern: 'mcdonalds', name: 'マクドナルド' },
  { pattern: "mcdonald's", name: 'マクドナルド' },
  { pattern: 'マック', name: 'マクドナルド' },
  { pattern: '7spot', name: 'セブンイレブン' },
  { pattern: 'seven', name: 'セブンイレブン' },
  { pattern: 'lawson', name: 'ローソン' },
  { pattern: 'familymart', name: 'ファミリーマート' },
]

/**
 * 訪問記録から場所名を取得
 * 優先順位: place.display_name > WiFi推測 > fallback
 */
export const getPlaceName = (visit: OverlandVisit, fallback = '不明な場所'): string => {
  if (visit.place?.display_name) return visit.place.display_name

  const wifi = visit.wifi
  if (!wifi) return fallback

  const lower = wifi.toLowerCase()
  const match = WIFI_PLACE_MAP.find((m) => lower.includes(m.pattern))
  return match?.name ?? wifi
}
